import { html, nothing } from "lit";
import type { FleetTodoItem, FleetTodoStatus } from "../controllers/fleet-todos.ts";

export type FleetTodosStripProps = {
  todos: FleetTodoItem[];
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  onStatusChange?: (id: string, status: FleetTodoStatus) => void;
  busy?: boolean;
};

function statusIcon(status: FleetTodoStatus): string {
  switch (status) {
    case "completed":
      return "✓";
    case "in_progress":
      return "◐";
    case "cancelled":
      return "—";
    case "pending":
    default:
      return "○";
  }
}

function statusClass(status: FleetTodoStatus): string {
  return `fleet-todos-strip__item--${status}`;
}

function nextStatusOnClick(current: FleetTodoStatus): FleetTodoStatus {
  if (current === "pending") {
    return "in_progress";
  }
  if (current === "in_progress") {
    return "completed";
  }
  if (current === "completed") {
    return "pending";
  }
  return "pending";
}

export function renderFleetTodosStrip(props: FleetTodosStripProps) {
  if (props.todos.length === 0) {
    return nothing;
  }
  const done = props.todos.filter((t) => t.status === "completed").length;
  const collapsed = props.collapsed === true;

  return html`
    <div class="fleet-todos-strip" role="region" aria-label="Implementation todos">
      <button
        type="button"
        class="fleet-todos-strip__header"
        aria-expanded=${!collapsed}
        @click=${props.onToggleCollapsed}
      >
        <span class="fleet-todos-strip__title">Todos</span>
        <span class="fleet-todos-strip__meta muted">${done}/${props.todos.length} done</span>
      </button>
      ${
        collapsed
          ? nothing
          : html`
              <ul class="fleet-todos-strip__list">
                ${props.todos.map(
                  (todo) => html`
                    <li class="fleet-todos-strip__item ${statusClass(todo.status)}">
                      <button
                        type="button"
                        class="fleet-todos-strip__check-btn"
                        title="Toggle status"
                        ?disabled=${!props.onStatusChange || props.busy}
                        @click=${() =>
                          props.onStatusChange?.(todo.id, nextStatusOnClick(todo.status))}
                      >
                        <span class="fleet-todos-strip__check" aria-hidden="true"
                          >${statusIcon(todo.status)}</span
                        >
                      </button>
                      <div class="fleet-todos-strip__body">
                        <span class="fleet-todos-strip__text">${todo.content}</span>
                        ${
                          todo.failureReason
                            ? html`<span
                                class="fleet-todos-strip__detail fleet-todos-strip__detail--fail"
                                >${todo.failureReason}</span
                              >`
                            : nothing
                        }
                        ${
                          todo.deliverablePath
                            ? html`<span class="fleet-todos-strip__detail mono"
                                >${todo.deliverablePath}</span
                              >`
                            : nothing
                        }
                        ${
                          todo.validationNotes?.length
                            ? html`<span class="fleet-todos-strip__detail muted"
                                >${todo.validationNotes[todo.validationNotes.length - 1]}</span
                              >`
                            : nothing
                        }
                      </div>
                    </li>
                  `,
                )}
              </ul>
            `
      }
    </div>
  `;
}
